import { useId } from 'react';
import type { ReactNode } from 'react';
import { isHumanBase, normalizeAvatar } from './avatarOptions';
import type { AvatarConfig, ResolvedAvatarConfig } from './types';
import './avatar.css';

/** Original Woxa Café characters. Pure SVG, deterministic, no external assets. */

const INK = '#3d2a20';
const WHITE = '#fffdf8';

type Palette = { hi: string; mid: string; lo: string; line: string; inner: string };
const PALETTES: Record<string, Palette> = {
  cream: { hi: '#fffdf6', mid: '#f6e7cf', lo: '#d2b08b', line: '#c9a782', inner: '#f6d0bd' },
  pumpkin: { hi: '#ffc988', mid: '#ec8a3c', lo: '#b04e20', line: '#a9491f', inner: '#ffdcae' },
  caramel: { hi: '#ffe4b3', mid: '#e3ae6a', lo: '#a96f37', line: '#9e6634', inner: '#fff0d4' },
  mocha: { hi: '#cfa688', mid: '#9c6c50', lo: '#62402e', line: '#583828', inner: '#ead2bd' },
  matcha: { hi: '#f0f4d3', mid: '#c1d292', lo: '#7f9759', line: '#748b52', inner: '#f8f8e4' },
  berry: { hi: '#ffe6e9', mid: '#f4afba', lo: '#c46c7d', line: '#b9677a', inner: '#fff3f4' },
  taro: { hi: '#f3ebfb', mid: '#cdb9e7', lo: '#8c72b0', line: '#8470a6', inner: '#faf5fe' },
  blue: { hi: '#e8f3fb', mid: '#abcde6', lo: '#628fb5', line: '#5f88aa', inner: '#f4f9fd' },
};

type SkinPalette = { hi: string; mid: string; lo: string; line: string };
const SKINS: Record<string, SkinPalette> = {
  'skin-1': { hi: '#fff3ea', mid: '#ffdfcb', lo: '#e8b598', line: '#d49c7f' },
  'skin-2': { hi: '#ffeedd', mid: '#f9cfa9', lo: '#dea075', line: '#c98b63' },
  'skin-3': { hi: '#f8d6ae', mid: '#e2ad7d', lo: '#b97b4c', line: '#a66b40' },
  'skin-4': { hi: '#dfac7f', mid: '#b98053', lo: '#8a5633', line: '#774829' },
  'skin-5': { hi: '#ad7752', mid: '#875739', lo: '#5c3922', line: '#4e2f1c' },
};

type HairPalette = { hi: string; mid: string; lo: string; line: string };
const HAIRS: Record<string, HairPalette> = {
  black: { hi: '#6a6170', mid: '#36303b', lo: '#1c181f', line: '#141116' },
  'dark-brown': { hi: '#8a6049', mid: '#553829', lo: '#311f16', line: '#281910' },
  brown: { hi: '#c08a5f', mid: '#8a5a39', lo: '#5a3621', line: '#4c2d1a' },
  caramel: { hi: '#f4cb92', mid: '#cf9252', lo: '#975c2c', line: '#86521f' },
  blonde: { hi: '#fff3c4', mid: '#efcf7e', lo: '#c49a48', line: '#ad873c' },
  pink: { hi: '#ffdbe5', mid: '#f1a3b8', lo: '#c76f8b', line: '#b2617c' },
  ash: { hi: '#ebe8ee', mid: '#b8b3be', lo: '#7f7986', line: '#6e6875' },
};

export interface AvatarAnchor {
  /** Centre and top of the head; hats are translated to this point. */
  headX: number; headTop: number; hatScale: number;
  /** Half width of the head at ear level, used by headphones. */
  sideHalf: number; earY: number;
  eyeY: number; eyeDx: number; mouthY: number; cheekY: number; cheekDx: number;
  neckY: number; neckHalf: number; handY: number; handDx: number;
  apronTop: number; apronBottom: number; apronHalf: number;
}

const HUMAN_ANCHOR: AvatarAnchor = { headX: 60, headTop: 30, hatScale: 1.15, sideHalf: 27, earY: 58, eyeY: 58, eyeDx: 10, mouthY: 68.5, cheekY: 65.5, cheekDx: 16.5, neckY: 80, neckHalf: 14, handY: 93, handDx: 11, apronTop: 84, apronBottom: 104, apronHalf: 15 };

export const AVATAR_ANCHORS: Record<'ghost' | 'pumpkin' | 'cat' | 'coffee-cup' | 'bear' | 'boy' | 'girl', AvatarAnchor> = {
  ghost: { headX: 60, headTop: 34, hatScale: 1, sideHalf: 28, earY: 58, eyeY: 57, eyeDx: 10.5, mouthY: 66, cheekY: 64, cheekDx: 17, neckY: 75, neckHalf: 27.5, handY: 89, handDx: 11, apronTop: 77, apronBottom: 100, apronHalf: 22 },
  pumpkin: { headX: 60, headTop: 46, hatScale: 1.08, sideHalf: 35, earY: 70, eyeY: 67, eyeDx: 12, mouthY: 76.5, cheekY: 74.5, cheekDx: 21, neckY: 86, neckHalf: 31, handY: 97, handDx: 11, apronTop: 89, apronBottom: 105, apronHalf: 21 },
  cat: { headX: 60, headTop: 34, hatScale: 1, sideHalf: 28, earY: 56, eyeY: 56, eyeDx: 11, mouthY: 64.5, cheekY: 62.5, cheekDx: 18, neckY: 79, neckHalf: 17, handY: 94, handDx: 11, apronTop: 82, apronBottom: 106, apronHalf: 17 },
  'coffee-cup': { headX: 60, headTop: 38, hatScale: 1.12, sideHalf: 30, earY: 56, eyeY: 66, eyeDx: 10.5, mouthY: 75, cheekY: 73, cheekDx: 17, neckY: 54, neckHalf: 28.5, handY: 93, handDx: 11, apronTop: 82, apronBottom: 104, apronHalf: 21 },
  bear: { headX: 60, headTop: 30, hatScale: 1, sideHalf: 26, earY: 55, eyeY: 54, eyeDx: 10, mouthY: 66.5, cheekY: 62.5, cheekDx: 17, neckY: 79, neckHalf: 17.5, handY: 94, handDx: 11, apronTop: 82, apronBottom: 106, apronHalf: 18 },
  boy: HUMAN_ANCHOR,
  girl: HUMAN_ANCHOR,
};

/** Hats that cover the crown: hair above this local y (× hatScale) is tucked under the hat. */
const HAT_TUCK: Record<string, number> = { witch: 5, beanie: 4, barista: 8 };

type Paint = (name: string) => string;
type Ctx = { a: AvatarAnchor; pal: Palette; paint: Paint; cfg: ResolvedAvatarConfig; skin: SkinPalette; hair: HairPalette; human: boolean };

/* ---------------------------------------------------------------- background */
function Background({ id }: { id: string }) {
  switch (id) {
    case 'pumpkin-blob':
      return <g><path d="M60 6 C88 4 113 22 114 52 C116 84 98 112 64 114 C30 116 6 96 6 62 C6 30 30 8 60 6Z" fill="#fbd8b0" /><path d="M24 30 C34 18 46 14 58 13" fill="none" stroke="#fde8cf" strokeWidth="4" strokeLinecap="round" /></g>;
    case 'mint-square':
      return <g><rect x="6" y="6" width="108" height="108" rx="32" fill="#dcebd8" /><circle cx="94" cy="26" r="3" fill="#eef6eb" /><circle cx="22" cy="92" r="2" fill="#eef6eb" /></g>;
    case 'taro-flower':
      return <g fill="#ebe1f5">
        {[0, 45, 90, 135, 180, 225, 270, 315].map(d => <circle key={d} cx={60 + 40 * Math.cos((d * Math.PI) / 180)} cy={60 + 40 * Math.sin((d * Math.PI) / 180)} r="17" />)}
        <circle cx="60" cy="60" r="44" />
      </g>;
    case 'sky-arch':
      return <g><path d="M10 114 V58 A50 50 0 0 1 110 58 V114Z" fill="#dbeaf3" /><path d="M88 22 A9 9 0 1 0 97 33 A7 7 0 1 1 88 22Z" fill="#fff6dc" /><circle cx="26" cy="40" r="1.6" fill="#fff" /><circle cx="36" cy="26" r="1.1" fill="#fff" /></g>;
    default: // cream-circle
      return <g><circle cx="60" cy="60" r="56" fill="#f7e7cf" /><circle cx="60" cy="60" r="50" fill="none" stroke="#f1dbbd" strokeWidth="1.5" strokeDasharray="2 5" strokeLinecap="round" /></g>;
  }
}

/* ---------------------------------------------------------------- bodies */
function SideArms({ a, paint, pal }: Ctx) {
  const y = a.handY - 6;
  return <g fill={paint('body')} stroke={pal.line} strokeWidth="1.2">
    <ellipse cx={a.headX - a.neckHalf + 1} cy={y} rx="4.5" ry="6" transform={`rotate(28 ${a.headX - a.neckHalf + 1} ${y})`} />
    <ellipse cx={a.headX + a.neckHalf - 1} cy={y} rx="4.5" ry="6" transform={`rotate(-28 ${a.headX + a.neckHalf - 1} ${y})`} />
  </g>;
}

/* ---------------------------------------------------------------- humans (boy / girl) */
const HEAD = 'M35 54 C35 37 46 30 60 30 C74 30 85 37 85 54 C85 68 75 78 60 78 C45 78 35 68 35 54Z';

function HumanBody({ pal, paint, skin, cfg }: Ctx) {
  const girl = cfg.base === 'girl';
  const sk = { fill: paint('skin'), stroke: skin.line, strokeWidth: 1.1 };
  const shirt = { fill: paint('body'), stroke: pal.line, strokeWidth: 1.1, strokeLinejoin: 'round' as const };
  const free = cfg.holding === 'none';
  return <g>
    {/* legs + shoes */}
    {girl
      ? <g><path d="M51 99 H56.5 V106 H51Z M63.5 99 H69 V106 H63.5Z" {...sk} /><g fill="#7a4a33" stroke="#553222" strokeWidth=".9"><ellipse cx="53.4" cy="107" rx="5.4" ry="2.7" /><ellipse cx="66.6" cy="107" rx="5.4" ry="2.7" /></g></g>
      : <g><path d="M47 98 H59 V105.5 H47Z M61 98 H73 V105.5 H61Z" fill="#617390" stroke="#47556d" strokeWidth="1" strokeLinejoin="round" /><path d="M60 99 V104" stroke="#47556d" strokeWidth=".8" /><g fill="#5a3b2b" stroke="#3a261b" strokeWidth=".9"><ellipse cx="52.6" cy="107" rx="6.6" ry="2.9" /><ellipse cx="67.4" cy="107" rx="6.6" ry="2.9" /></g></g>}
    {/* arms (behind the torso edge) */}
    {free
      ? <g><path d="M44 82 Q37 86 36 95 L41.5 96 Q42 90 46 87Z M76 82 Q83 86 84 95 L78.5 96 Q78 90 74 87Z" {...shirt} /><circle cx="38.6" cy="97.4" r="3.6" {...sk} /><circle cx="81.4" cy="97.4" r="3.6" {...sk} /></g>
      : <path d="M44 82 Q38 88 45.5 95.5 L50 91 Q46 88 47 85Z M76 82 Q82 88 74.5 95.5 L70 91 Q74 88 73 85Z" {...shirt} />}
    {/* neck */}
    <rect x="55" y="73" width="10" height="9" rx="3" fill={skin.lo} />
    {/* torso */}
    {girl
      ? <g>
          <path d="M46 80 Q60 76.5 74 80 Q79 82 79 88 L83.5 101 Q60 106.5 36.5 101 L41 88 Q41 82 46 80Z" {...shirt} />
          <path d="M41.5 90.5 Q60 93.5 78.5 90.5" fill="none" stroke={pal.lo} strokeWidth="1.2" opacity=".55" strokeLinecap="round" />
          <path d="M39 100 Q60 104.5 81 100" fill="none" stroke={pal.hi} strokeWidth="1.4" opacity=".8" strokeLinecap="round" />
          <path d="M60 80.5 Q54 86 49.5 80.6 Q54 78.6 60 79.4Z M60 80.5 Q66 86 70.5 80.6 Q66 78.6 60 79.4Z" fill={WHITE} stroke={pal.line} strokeWidth=".8" strokeLinejoin="round" />
        </g>
      : <g>
          <path d="M45 80 Q60 76.5 75 80 Q80.5 82 80.5 89 L80 100 Q60 102.5 40 100 L39.5 89 Q39.5 82 45 80Z" {...shirt} />
          <path d="M53.5 79 L60 85.5 L66.5 79 L63.5 78 L60 81.5 L56.5 78Z" fill={WHITE} stroke={pal.line} strokeWidth=".8" strokeLinejoin="round" />
          <g fill={pal.lo} opacity=".6"><circle cx="60" cy="89" r=".9" /><circle cx="60" cy="94" r=".9" /></g>
        </g>}
    <path d="M44 85 Q42.5 91 43.5 97" fill="none" stroke={pal.hi} strokeWidth="2.6" strokeLinecap="round" opacity=".75" />
    {/* head */}
    <ellipse cx="35.5" cy="59" rx="4.2" ry="5.4" {...sk} /><ellipse cx="84.5" cy="59" rx="4.2" ry="5.4" {...sk} />
    <path d="M35.5 56.5 Q34 59 35.8 61.5 M84.5 56.5 Q86 59 84.2 61.5" fill="none" stroke={skin.lo} strokeWidth="1" strokeLinecap="round" />
    <path d={HEAD} fill={paint('skin')} stroke={skin.line} strokeWidth="1.2" />
    <path d="M44 72 Q52 77.5 60 77.6" fill="none" stroke={skin.hi} strokeWidth="1.6" strokeLinecap="round" opacity=".5" />
    <path d="M58.8 63.4 Q60 64.4 61.2 63.4" fill="none" stroke={skin.lo} strokeWidth="1.1" strokeLinecap="round" />
  </g>;
}

/* Hair: the crown outline shared by most styles runs from the left temple, over the top, to the right temple. */
const CROWN = 'M34.5 54 C33 36 45 26.5 60 26.5 C75 26.5 87 36 85.5 54';
const CAP_BACK = 'M32 60 C29 37 42 24.5 60 24.5 C78 24.5 91 37 88 60 Q86 63 84 60 L36 60 Q34 63 32 60Z';
const LONG_BACK = 'M31 56 C28 36 42 24 60 24 C78 24 92 36 89 56 L91 92 Q91 99 84 99 L36 99 Q29 99 29 92Z';
const mirror = (d: string) => d.replace(/(-?\d+(?:\.\d+)?) (-?\d+(?:\.\d+)?)/g, (_, x, y) => `${+(120 - Number(x)).toFixed(2)} ${y}`);
const LOCK_LONG = 'M36.5 44 Q31 56 32 70 Q32.5 78 30 84 Q36 84.5 38 76 Q39.5 64 38.5 50Z';
const LOCK_WAVY = 'M36.5 44 Q30 52 33 60 Q29 68 33 75 Q30 81 31 86 Q37 84.5 38.5 76 Q35.5 69 38.5 62 Q36 55 38.5 50Z';
const LOCK_BOB = 'M36 46 Q31 58 32 71 Q33 77 37.5 78 Q38.5 66 39 50Z';
const LOCK_SHORT = 'M36.5 46 Q33 54 34 63 Q36.5 62 38 56 Q38.5 51 38 48Z';

function HairBack({ style, hair, paint }: { style: string; hair: HairPalette; paint: Paint }) {
  const f = { fill: paint('hair'), stroke: hair.line, strokeWidth: 1.1, strokeLinejoin: 'round' as const };
  const shade = (d: string) => <path d={d} fill={hair.lo} opacity=".35" />;
  switch (style) {
    case 'buzz':
      return <path d="M34 58 C31 38 43 28 60 28 C77 28 89 38 86 58Z" {...f} />;
    case 'curly':
      return <g {...f}><path d={CAP_BACK} />{[[33, 50, 6], [87, 50, 6], [33.5, 58, 5], [86.5, 58, 5]].map(([x, y, r]) => <circle key={`${x}-${y}`} cx={x} cy={y} r={r} />)}</g>;
    case 'long':
      return <g><path d={LONG_BACK} {...f} />{shade('M42 76 Q60 84 78 76 L80 98 L40 98Z')}</g>;
    case 'wavy':
      return <g><path d="M31 56 C28 36 42 24 60 24 C78 24 92 36 89 56 Q94 64 89 72 Q94 80 90 88 Q93 96 86 99 L34 99 Q27 96 30 88 Q26 80 31 72 Q26 64 31 56Z" {...f} />{shade('M42 76 Q60 84 78 76 L80 98 L40 98Z')}</g>;
    case 'bob':
      return <path d="M30 56 C28 36 42 24 60 24 C78 24 92 36 90 56 Q91 70 88 77 Q84 80.5 79.5 76 Q60 58 40.5 76 Q36 80.5 32 77 Q29 70 30 56Z" {...f} />;
    case 'ponytail':
      return <g {...f}><path d="M81 33 Q98 33 98.5 52 Q99 68 91 81 Q90.5 71 88.5 63 Q86 51 80 45Z" /><path d={CAP_BACK} /><path d="M90 45 Q94 56 92 68" fill="none" stroke={hair.hi} strokeWidth="1.4" opacity=".7" strokeLinecap="round" /></g>;
    case 'pigtails': {
      const tail = 'M35 46 Q20 49 21 65 Q22 77 28 84 Q29.5 74 33.5 68 Q38.5 60 37.5 50Z';
      return <g {...f}><path d={tail} /><path d={mirror(tail)} /><path d={CAP_BACK} /><path d="M25 58 Q24 66 27 74 M95 58 Q96 66 93 74" fill="none" stroke={hair.hi} strokeWidth="1.3" opacity=".7" strokeLinecap="round" /></g>;
    }
    case 'bun':
      return <g {...f}><circle cx="60" cy="20" r="10" /><path d="M54 15 Q58 12 63 13" fill="none" stroke={hair.hi} strokeWidth="1.5" opacity=".8" strokeLinecap="round" /><path d={CAP_BACK} /></g>;
    case 'spiky':
    case 'side-part':
    case 'mid-part':
    default: // short
      return <path d={CAP_BACK} {...f} />;
  }
}

function HairFront({ style, hair, paint }: { style: string; hair: HairPalette; paint: Paint }) {
  const f = { fill: paint('hair'), stroke: hair.line, strokeWidth: 1.1, strokeLinejoin: 'round' as const };
  const shine = (d = 'M42 37 Q49 30.5 57 30') => <path d={d} fill="none" stroke={hair.hi} strokeWidth="2.4" strokeLinecap="round" opacity=".75" />;
  const strands = (d: string) => <path d={d} fill="none" stroke={hair.lo} strokeWidth="1" strokeLinecap="round" opacity=".55" />;
  const tie = (x: number, y: number) => <g key={`${x}-${y}`}><circle cx={x} cy={y} r="3.1" fill={paint('orange')} stroke="#a9491f" strokeWidth=".8" /><circle cx={x - .9} cy={y - 1} r=".9" fill="#ffe0b8" /></g>;
  const softFringe = `${CROWN} C84 46 79 41 73 39 Q68 45 61 41 Q54 45 48 40 Q41 42 37 48 Q35.5 51 34.5 54Z`;
  const sweptFringe = `${CROWN} C85 48 82 44 77 42 Q66 43.5 57 39.5 Q51 37 47.5 35.5 Q44 41.5 39 45 Q36 48 34.5 54Z`;
  switch (style) {
    case 'side-part':
      return <g><path d={sweptFringe} {...f} />{strands('M48 36 Q60 40 74 41 M52 33 Q64 36 79 39')}<path d="M47.5 35.5 Q49 30.5 52 28" fill="none" stroke={hair.lo} strokeWidth="1.1" strokeLinecap="round" />{shine('M55 31 Q64 28.5 72 31')}</g>;
    case 'spiky':
      return <g><path d="M34.5 54 C33 44 35 37 39 33 L36 24 L45 28 L47 19 L54 25 L60 16 L65.5 25 L73 19 L74.5 28 L83.5 25 L81 34 C85 39 86.5 46 85.5 54 L82 47 L77 43 L72 46.5 L67 41 L61 46 L55 41 L49 46 L44 42 L39 47.5Z" {...f} />{strands('M47 26 L49 33 M60 19 L60 30 M72.5 23 L70.5 32')}{shine('M43 36 Q50 31 57 30.5')}</g>;
    case 'curly': {
      const curls: [number, number, number][] = [[38, 47, 5.8], [40.5, 38, 7], [47.5, 31, 7.5], [56.5, 27.5, 8], [65.5, 27.5, 8], [74, 31, 7.5], [80, 38, 7], [82.5, 47, 5.8], [45, 43, 5.4], [52.5, 42.5, 5.8], [60, 41.5, 5.8], [67.5, 42.5, 5.8], [75, 43, 5.4]];
      return <g><path d={`${CROWN} L84 46 L36 46Z`} fill={paint('hair')} />{curls.map(([x, y, r]) => <circle key={`${x}-${y}`} cx={x} cy={y} r={r} {...f} />)}{[[47.5, 31], [56.5, 27.5], [65.5, 27.5], [60, 41.5]].map(([x, y]) => <path key={`h${x}`} d={`M${x - 3} ${y - 1.5} Q${x} ${y - 4.5} ${x + 3} ${y - 2}`} fill="none" stroke={hair.hi} strokeWidth="1.3" strokeLinecap="round" opacity=".75" />)}</g>;
    }
    case 'buzz':
      return <g><path d="M35.2 53 C34.5 37 46 29.2 60 29.2 C74 29.2 85.5 37 84.8 53 Q83.5 49.5 82 47 L80.5 45.6 C76 42.5 70 41.6 64 41.3 L62 42 L60 41.2 L58 42 L56 41.3 C50 41.6 44 42.5 39.5 45.6 L38 47 Q36.5 49.5 35.2 53Z" fill={paint('hair')} stroke={hair.lo} strokeWidth=".8" strokeLinejoin="round" opacity=".92" /><g fill={hair.hi} opacity=".45">{[[46, 35], [53, 33], [61, 32.5], [69, 34], [75, 37], [42, 41], [50, 38], [58, 37], [66, 38]].map(([x, y]) => <circle key={`${x}-${y}`} cx={x} cy={y} r=".7" />)}</g>{shine('M45 34 Q51 31 57 31')}</g>;
    case 'mid-part':
      return <g><path d={`${CROWN} C85 50 82 45.5 76 44.5 Q66 43.5 61.2 36 L60 38 L58.8 36 Q54 43.5 44 44.5 Q38 45.5 34.5 54Z`} {...f} />{strands('M58.5 32 Q53 40 43 44 M61.5 32 Q67 40 77 44')}{shine('M43 36 Q49 30.5 56 30')}</g>;
    case 'long':
      return <g><path d={LOCK_LONG} {...f} /><path d={mirror(LOCK_LONG)} {...f} /><path d={softFringe} {...f} />{strands('M48 40 Q50 34 53 31 M61 41 Q62 35 64 31')}{shine()}</g>;
    case 'wavy':
      return <g><path d={LOCK_WAVY} {...f} /><path d={mirror(LOCK_WAVY)} {...f} /><path d={sweptFringe} {...f} />{strands('M48 36 Q60 40 74 41')}{shine('M55 31 Q64 28.5 72 31')}</g>;
    case 'bob':
      return <g><path d={LOCK_BOB} {...f} /><path d={mirror(LOCK_BOB)} {...f} /><path d={`${CROWN} C85 49 84 45.5 81 44.5 Q60 47 39 44.5 Q36 45.5 34.5 54Z`} {...f} />{strands('M47 45 V40 M60 46 V39 M73 45 V40')}{shine()}</g>;
    case 'ponytail':
      return <g><path d={LOCK_SHORT} {...f} /><path d={sweptFringe} {...f} />{strands('M48 36 Q60 40 74 41')}{shine('M55 31 Q64 28.5 72 31')}{tie(83, 37)}</g>;
    case 'pigtails':
      return <g><path d={softFringe} {...f} />{strands('M48 40 Q50 34 53 31 M61 41 Q62 35 64 31')}{shine()}{tie(35.5, 47)}{tie(84.5, 47)}</g>;
    case 'bun':
      return <g><path d={LOCK_SHORT} {...f} /><path d={mirror(LOCK_SHORT)} {...f} /><path d={softFringe} {...f} />{strands('M48 40 Q50 34 53 31 M61 41 Q62 35 64 31')}{shine()}<path d="M54 27.4 Q60 25.6 66 27.4" fill="none" stroke="#d96a2b" strokeWidth="2.4" strokeLinecap="round" /></g>;
    default: // short
      return <g><path d={`${CROWN} C84 47 80 43 76 41 Q72 46 66 43 Q61 47 55 43 Q49 47 44 43 Q39 46 36.5 50 Q35 52 34.5 54Z`} {...f} />{strands('M55 43 Q56 36 59 31 M66 43 Q66 36 68 32')}{shine()}</g>;
  }
}

function Body(ctx: Ctx) {
  const { pal, paint, cfg } = ctx;
  const arms = cfg.holding === 'none' ? <SideArms {...ctx} /> : null;
  switch (cfg.base) {
    case 'boy':
    case 'girl':
      return <HumanBody {...ctx} />;
    case 'pumpkin':
      return <g>
        <path d="M58 48 Q55 40 61 34 L66 37 Q61 41 62.5 48Z" fill="#6f7c4c" stroke="#4f5937" strokeWidth="1.2" strokeLinejoin="round" />
        <path d="M62 41 Q73 31 82 39 Q72 45 62 41Z" fill="#8e9c62" />
        <ellipse cx="45" cy="77" rx="22" ry="28" fill={paint('body')} stroke={pal.line} strokeWidth="1.2" />
        <ellipse cx="75" cy="77" rx="22" ry="28" fill={paint('body')} stroke={pal.line} strokeWidth="1.2" />
        <ellipse cx="60" cy="77" rx="21" ry="31" fill={paint('body')} stroke={pal.line} strokeWidth="1.2" />
        <path d="M51 52 Q42 64 45 80" fill="none" stroke={pal.hi} strokeWidth="3.5" strokeLinecap="round" opacity=".8" />
        <path d="M40 98 Q60 108 80 98" fill="none" stroke={pal.lo} strokeWidth="2" opacity=".35" strokeLinecap="round" />
        {arms}
      </g>;
    case 'cat':
      return <g>
        <path d="M76 100 Q94 102 94 86 Q94 76 86 78" fill="none" stroke={pal.line} strokeWidth="8" strokeLinecap="round" />
        <path d="M76 100 Q94 102 94 86 Q94 76 86 78" fill="none" stroke={pal.mid} strokeWidth="5.5" strokeLinecap="round" />
        <ellipse cx="60" cy="93" rx="20" ry="15" fill={paint('body')} stroke={pal.line} strokeWidth="1.2" />
        <ellipse cx="51" cy="106" rx="6" ry="3.5" fill={pal.hi} stroke={pal.line} strokeWidth="1.1" />
        <ellipse cx="69" cy="106" rx="6" ry="3.5" fill={pal.hi} stroke={pal.line} strokeWidth="1.1" />
        <path d="M35 47 L37 21 L56 35Z" fill={paint('body')} stroke={pal.line} strokeWidth="1.2" strokeLinejoin="round" />
        <path d="M85 47 L83 21 L64 35Z" fill={paint('body')} stroke={pal.line} strokeWidth="1.2" strokeLinejoin="round" />
        <path d="M39.5 40 L40.5 27 L50 35Z M80.5 40 L79.5 27 L70 35Z" fill={pal.inner} />
        <ellipse cx="60" cy="56" rx="28" ry="23" fill={paint('body')} stroke={pal.line} strokeWidth="1.2" />
        <path d="M42 46 Q46 38 55 36" fill="none" stroke={pal.hi} strokeWidth="3" strokeLinecap="round" opacity=".9" />
        <path d="M57.8 60.5 H62.2 L60 63Z" fill="#d97a7f" stroke="#b9606a" strokeWidth=".6" strokeLinejoin="round" />
        <path d="M33 59 L42 60 M33 64 L42 62.5 M87 59 L78 60 M87 64 L78 62.5" stroke={pal.lo} strokeWidth="1" strokeLinecap="round" opacity=".7" />
        {arms}
      </g>;
    case 'bear':
      return <g>
        <ellipse cx="60" cy="94" rx="21" ry="14" fill={paint('body')} stroke={pal.line} strokeWidth="1.2" />
        <ellipse cx="51" cy="106" rx="6" ry="3.5" fill={pal.hi} stroke={pal.line} strokeWidth="1.1" />
        <ellipse cx="69" cy="106" rx="6" ry="3.5" fill={pal.hi} stroke={pal.line} strokeWidth="1.1" />
        <circle cx="39" cy="35" r="9" fill={paint('body')} stroke={pal.line} strokeWidth="1.2" />
        <circle cx="81" cy="35" r="9" fill={paint('body')} stroke={pal.line} strokeWidth="1.2" />
        <circle cx="39" cy="35" r="4.8" fill={pal.inner} /><circle cx="81" cy="35" r="4.8" fill={pal.inner} />
        <circle cx="60" cy="56" r="26" fill={paint('body')} stroke={pal.line} strokeWidth="1.2" />
        <path d="M43 46 Q47 37 56 35" fill="none" stroke={pal.hi} strokeWidth="3" strokeLinecap="round" opacity=".9" />
        <ellipse cx="60" cy="65" rx="10" ry="7.2" fill={pal.inner} opacity=".95" />
        <ellipse cx="60" cy="61.6" rx="3.2" ry="2.3" fill={INK} />
        <path d="M60 63.8 V65.2" stroke={INK} strokeWidth="1.2" strokeLinecap="round" />
        {arms}
      </g>;
    case 'coffee-cup':
      return <g>
        <ellipse cx="60" cy="106" rx="38" ry="5.5" fill={paint('cream')} stroke="#d4bc9c" strokeWidth="1.1" />
        <path d="M87 58 C102 56 104 82 85 84" fill="none" stroke={pal.line} strokeWidth="8" strokeLinecap="round" />
        <path d="M87 58 C102 56 104 82 85 84" fill="none" stroke={pal.mid} strokeWidth="5" strokeLinecap="round" />
        <path d="M31 46 L37 99 Q38 105 46 105 H74 Q82 105 83 99 L89 46Z" fill={paint('body')} stroke={pal.line} strokeWidth="1.2" strokeLinejoin="round" />
        <path d="M37 54 L41 92" stroke={pal.hi} strokeWidth="3.5" strokeLinecap="round" opacity=".85" />
        <ellipse cx="60" cy="46" rx="29" ry="7" fill={pal.hi} stroke={pal.line} strokeWidth="1.2" />
        <ellipse cx="60" cy="46.6" rx="25" ry="5" fill="#6b4430" />
        <path d="M60 50 C52 46 54 41.5 58 43 Q60 44 60 45 Q60 44 62 43 C66 41.5 68 46 60 50Z" fill="#f4e2c6" />
        {cfg.hat === 'none' && <path d="M52 36 Q48 31 52 26 Q56 21 52 16 M66 36 Q62 31 66 26 Q70 21 66 16" fill="none" stroke="#c9b298" strokeWidth="2" strokeLinecap="round" opacity=".7" />}
        {arms}
      </g>;
    default: // ghost
      return <g>
        <path d="M32 100 Q33 86 32 64 C30 24 90 24 88 64 Q87 86 90 100 Q83 108 75 101 Q67 109 60 102 Q53 109 45 101 Q37 108 32 100Z" fill={paint('body')} stroke={pal.line} strokeWidth="1.2" strokeLinejoin="round" />
        <path d="M41 60 Q40 43 54 38" fill="none" stroke={pal.hi} strokeWidth="4" strokeLinecap="round" />
        <path d="M38 98 Q45 94 50 99 M70 99 Q76 94 82 98" fill="none" stroke={pal.lo} strokeWidth="1.4" opacity=".35" strokeLinecap="round" />
        {arms}
      </g>;
  }
}

/* ---------------------------------------------------------------- face */
function Eye({ kind, x, y, side }: { kind: string; x: number; y: number; side: 'l' | 'r' }) {
  const arc = (up: boolean) => <path d={up ? `M${x - 4} ${y + 1.5} Q${x} ${y - 3.5} ${x + 4} ${y + 1.5}` : `M${x - 4} ${y - .5} Q${x} ${y + 3.5} ${x + 4} ${y - .5}`} fill="none" stroke={INK} strokeWidth="2.3" strokeLinecap="round" />;
  switch (kind) {
    case 'dot':
      return <g><ellipse cx={x} cy={y} rx="3" ry="3.8" fill={INK} /><circle cx={x + 1} cy={y - 1.4} r="1" fill={WHITE} /></g>;
    case 'happy':
      return arc(true);
    case 'sleepy':
      return <g>{arc(false)}<path d={`M${side === 'l' ? x - 4 : x + 4} ${y - .5} l${side === 'l' ? -1.6 : 1.6} -1.4`} stroke={INK} strokeWidth="1.4" strokeLinecap="round" /></g>;
    case 'wink':
      return side === 'r' ? arc(true) : <Eye kind="sparkle" x={x} y={y} side={side} />;
    case 'heart':
      return <g><path d={`M${x} ${y + 3.6} C${x - 6.2} ${y - .4} ${x - 3.6} ${y - 5.8} ${x} ${y - 2.6} C${x + 3.6} ${y - 5.8} ${x + 6.2} ${y - .4} ${x} ${y + 3.6}Z`} fill="#c8484e" stroke="#9e3439" strokeWidth=".7" strokeLinejoin="round" /><circle cx={x - 2} cy={y - 1.8} r=".9" fill={WHITE} /></g>;
    default: // sparkle
      return <g><ellipse cx={x} cy={y} rx="4" ry="5" fill={INK} /><circle cx={x + 1.3} cy={y - 1.8} r="1.6" fill={WHITE} /><circle cx={x - 1.3} cy={y + 2} r=".8" fill={WHITE} opacity=".85" /></g>;
  }
}

function Mouth({ kind, x, y }: { kind: string; x: number; y: number }) {
  const line = { fill: 'none', stroke: INK, strokeWidth: 2, strokeLinecap: 'round' as const, strokeLinejoin: 'round' as const };
  const smile = <path d={`M${x - 3.5} ${y} Q${x} ${y + 3.5} ${x + 3.5} ${y}`} {...line} />;
  switch (kind) {
    case 'open':
      return <g><path d={`M${x - 4.2} ${y - 1} Q${x} ${y + 7.5} ${x + 4.2} ${y - 1}Z`} fill={INK} strokeLinejoin="round" /><path d={`M${x - 2.3} ${y + 2.6} Q${x} ${y + 1.4} ${x + 2.3} ${y + 2.6} Q${x} ${y + 4.6} ${x - 2.3} ${y + 2.6}Z`} fill="#e9858a" /></g>;
    case 'cat':
      return <path d={`M${x - 5} ${y - .5} Q${x - 2.5} ${y + 3} ${x} ${y} Q${x + 2.5} ${y + 3} ${x + 5} ${y - .5}`} {...line} />;
    case 'o':
      return <g><ellipse cx={x} cy={y + 1} rx="2.4" ry="2.9" fill={INK} /><ellipse cx={x} cy={y + 2.2} rx="1.3" ry=".9" fill="#e9858a" /></g>;
    case 'tongue':
      return <g><path d={`M${x + .2} ${y + 1.6} Q${x + .2} ${y + 5.8} ${x + 2.4} ${y + 5.4} Q${x + 4.2} ${y + 5} ${x + 3.4} ${y + .9}`} fill="#e9858a" stroke={INK} strokeWidth="1" strokeLinejoin="round" />{smile}</g>;
    case 'fang':
      return <g>{smile}<path d={`M${x + .9} ${y + 2.2} L${x + 1.9} ${y + 5} L${x + 2.8} ${y + 1.8}Z`} fill={WHITE} stroke={INK} strokeWidth=".8" strokeLinejoin="round" /></g>;
    default:
      return smile;
  }
}

function Face({ a, cfg }: Ctx) {
  const cheek = cfg.cheeks === 'pink' ? '#f2939c' : cfg.cheeks === 'orange' ? '#f3a160' : null;
  return <g>
    {cheek && <g fill={cheek} opacity=".6"><ellipse cx={a.headX - a.cheekDx} cy={a.cheekY} rx="4.6" ry="2.7" /><ellipse cx={a.headX + a.cheekDx} cy={a.cheekY} rx="4.6" ry="2.7" /></g>}
    <Eye kind={cfg.eyes} x={a.headX - a.eyeDx} y={a.eyeY} side="l" />
    <Eye kind={cfg.eyes} x={a.headX + a.eyeDx} y={a.eyeY} side="r" />
    <Mouth kind={cfg.mouth} x={a.headX} y={a.mouthY} />
    {cfg.base === 'girl' && ['sparkle', 'dot', 'wink'].includes(cfg.eyes) && <path d={`M${a.headX - a.eyeDx - 3.4} ${a.eyeY - 3} l-2.2 -1.6${cfg.eyes === 'wink' ? '' : ` M${a.headX + a.eyeDx + 3.4} ${a.eyeY - 3} l2.2 -1.6`}`} stroke={INK} strokeWidth="1.3" strokeLinecap="round" />}
  </g>;
}

/* ---------------------------------------------------------------- accessories */
function Accessory({ a, paint, cfg, human }: Ctx) {
  const x = a.headX;
  switch (cfg.accessory) {
    case 'glasses': {
      const l = x - a.eyeDx, r = x + a.eyeDx, y = a.eyeY;
      return <g fill="none" stroke={INK} strokeWidth="1.7" strokeLinecap="round">
        <circle cx={l} cy={y} r="7.2" fill={WHITE} fillOpacity=".22" /><circle cx={r} cy={y} r="7.2" fill={WHITE} fillOpacity=".22" />
        <path d={`M${l + 7.2} ${y - 1} Q${x} ${y - 4} ${r - 7.2} ${y - 1} M${l - 7.2} ${y - 1.5} L${l - 10} ${y - 3} M${r + 7.2} ${y - 1.5} L${r + 10} ${y - 3}`} />
        <path d={`M${l - 4} ${y - 3} L${l - 1.5} ${y - 5}`} stroke={WHITE} strokeWidth="1.2" opacity=".8" />
      </g>;
    }
    case 'scarf': {
      const y = a.neckY, w = a.neckHalf, tail = x + w * .55 - 4;
      return <g>
        <path d={`M${tail} ${y + 2} l1 17 l9 -1 l-1 -16Z`} fill={paint('orange')} stroke="#a9491f" strokeWidth="1" strokeLinejoin="round" />
        <path d={`M${tail + 1} ${y + 19} l9 -1`} stroke="#ffd9a8" strokeWidth="2" strokeDasharray="1.5 1.5" />
        <path d={`M${x - w} ${y - 3} Q${x} ${y + 4} ${x + w} ${y - 3} L${x + w} ${y + 3.5} Q${x} ${y + 11} ${x - w} ${y + 3.5}Z`} fill={paint('orange')} stroke="#a9491f" strokeWidth="1" strokeLinejoin="round" />
        <path d={`M${x - w + 7} ${y - .5} L${x - w + 7} ${y + 6} M${x - 6} ${y + 1.5} L${x - 6} ${y + 7.8} M${x + w - 7} ${y - .5} L${x + w - 7} ${y + 6}`} stroke="#ffe0b8" strokeWidth="2.4" opacity=".9" />
      </g>;
    }
    case 'apron': {
      const t = a.apronTop, b = a.apronBottom, h = a.apronHalf;
      return <g>
        {human
          ? <path d={`M${x - 7.5} ${t + 1} L${x - 12.5} ${t - 3.6} M${x + 7.5} ${t + 1} L${x + 12.5} ${t - 3.6}`} fill="none" stroke="#5a3b2b" strokeWidth="2.2" strokeLinecap="round" />
          : <path d={`M${x - 7.5} ${t + 1} C${x - 8.5} ${t - 6} ${x + 8.5} ${t - 6} ${x + 7.5} ${t + 1}`} fill="none" stroke="#5a3b2b" strokeWidth="1.6" strokeLinecap="round" />}
        <path d={`M${x - h + 2} ${t + 9} q-3 2 -4.5 5 M${x + h - 2} ${t + 9} q3 2 4.5 5`} fill="none" stroke="#5a3b2b" strokeWidth="1.6" strokeLinecap="round" />
        <path d={`M${x - 9} ${t} H${x + 9} L${x + 10} ${t + 7} Q${x + h} ${t + 8} ${x + h - 1} ${b - 4} Q${x} ${b + 1} ${x - h + 1} ${b - 4} Q${x - h} ${t + 8} ${x - 10} ${t + 7}Z`} fill={paint('ink')} stroke="#2d1f17" strokeWidth=".9" strokeLinejoin="round" />
        <path d={`M${x - 10} ${t + 7} Q${x} ${t + 9} ${x + 10} ${t + 7}`} fill="none" stroke="#e8c79b" strokeWidth="1.3" />
        <path d={`M${x - 6} ${b - 9} H${x + 6} V${b - 5} Q${x} ${b - 3} ${x - 6} ${b - 5}Z`} fill="none" stroke="#e8c79b" strokeWidth="1.1" strokeLinejoin="round" />
        <ellipse cx={x} cy={t + 3.4} rx="2" ry="2.6" fill="#e8c79b" /><path d={`M${x} ${t + 1.2} Q${x - .8} ${t + 3.4} ${x} ${t + 5.6}`} stroke="#5a3b2b" strokeWidth=".7" fill="none" />
      </g>;
    }
    default:
      return null;
  }
}

/* ---------------------------------------------------------------- held items */
function HeldItem({ kind, paint }: { kind: string; paint: Paint }) {
  switch (kind) {
    case 'tea':
      return <g>
        <ellipse cx="0" cy="8.6" rx="12.5" ry="2.6" fill={paint('cream')} stroke="#ccb291" strokeWidth=".9" />
        <path d="M8.5 -2 Q15 -2 13.5 3.5 Q12 6 7 5.5" fill="none" stroke="#d8c2a4" strokeWidth="2.2" strokeLinecap="round" />
        <path d="M-10 -4 Q-10 8 0 8 Q10 8 10 -4Z" fill={paint('cream')} stroke="#ccb291" strokeWidth=".9" strokeLinejoin="round" />
        <path d="M-6 2 Q0 4 6 2" fill="none" stroke="#8ea563" strokeWidth="1.6" strokeLinecap="round" />
        <ellipse cx="0" cy="-4" rx="10" ry="2.4" fill="#d58b45" stroke="#ccb291" strokeWidth=".8" />
        <path d="M2 -4 Q-3 -1 -12 1" fill="none" stroke="#bfa98d" strokeWidth=".7" />
        <rect x="-15.5" y="0" width="5" height="6" rx="1" fill="#8ea563" transform="rotate(-8 -13 3)" />
      </g>;
    case 'cake':
      return <g>
        <path d="M-11 -2 L11 -6.5 V7 L-11 9.5Z" fill="#f5d6a2" stroke="#c99c66" strokeWidth=".9" strokeLinejoin="round" />
        <path d="M-11 3 L11 .5" stroke="#fff6ea" strokeWidth="2.4" />
        <path d="M-11 -2 L11 -6.5 L11 -3 Q8 -1 7 -3.5 Q5 0 2 -2.5 Q0 1 -3 -1 Q-5 2 -8 .5 Q-10 1.5 -11 1Z" fill="#f6b3be" stroke="#d97f8e" strokeWidth=".8" strokeLinejoin="round" />
        <path d="M2 -6.5 C-2 -6.5 -2 -12.5 2 -13 C6 -12.5 6 -6.5 2 -6.5Z" fill="#e1525e" stroke="#b33c47" strokeWidth=".8" />
        <path d="M0 -12.6 L2 -14.5 L4 -12.6" fill="none" stroke="#6f8a47" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round" />
        <circle cx="0.8" cy="-10.2" r=".7" fill="#ffd7d9" />
      </g>;
    case 'cookie':
      return <g>
        <circle cx="0" cy="0" r="10" fill={paint('caramel')} stroke="#a46b35" strokeWidth=".9" />
        <path d="M-6 -5 Q-2 -8.4 3 -7.4" fill="none" stroke="#ffe5bb" strokeWidth="1.5" strokeLinecap="round" opacity=".9" />
        <g fill="#5a3826"><ellipse cx="-4" cy="-1" rx="1.6" ry="1.2" /><ellipse cx="3" cy="-3" rx="1.4" ry="1.1" /><ellipse cx="4" cy="4" rx="1.6" ry="1.2" /><ellipse cx="-2" cy="5" rx="1.2" ry="1" /><ellipse cx="6.5" cy="0" rx="1" ry=".8" /></g>
      </g>;
    case 'coffee':
      return <g>
        <path d="M8 -3 H10 C16 -3 16 6 9.5 6 H8" fill="none" stroke="#b05325" strokeWidth="2.6" strokeLinecap="round" />
        <path d="M-9 -7 H9 L7.5 7.5 Q0 11.5 -7.5 7.5Z" fill={paint('orange')} stroke="#a9491f" strokeWidth=".9" strokeLinejoin="round" />
        <ellipse cx="0" cy="-7" rx="9" ry="2.6" fill="#ffd29a" />
        <ellipse cx="0" cy="-6.7" rx="7" ry="1.6" fill="#5a3a2a" />
        <path d="M-5.5 -2 L-4.5 5.5" stroke="#ffc58d" strokeWidth="1.6" strokeLinecap="round" opacity=".8" />
        <path d="M-1.6 2.8 C-4 1 -2.6 -1.4 0 .3 C2.6 -1.4 4 1 1.6 2.8 L0 4.2Z" fill="#fff0d8" opacity=".9" />
      </g>;
    default:
      return null;
  }
}

function Holding({ a, paint, pal, cfg, skin, human }: Ctx) {
  if (cfg.holding === 'none') return null;
  const x = a.headX, y = a.handY;
  const hand = human ? { fill: paint('skin'), stroke: skin.line } : { fill: paint('body'), stroke: pal.line };
  const r = human ? 3.7 : 4.4;
  return <g>
    <g transform={`translate(${x} ${y})`}><HeldItem kind={cfg.holding} paint={paint} /></g>
    <g strokeWidth="1.1">
      <ellipse data-hand="l" cx={x - a.handDx} cy={y + 1.5} rx={r} ry={r - .4} {...hand} />
      <ellipse data-hand="r" cx={x + a.handDx} cy={y + 1.5} rx={r} ry={r - .4} {...hand} />
    </g>
  </g>;
}

/* ---------------------------------------------------------------- hats (local: origin = head top, fit line ≈ y+8) */
function Hat({ a, paint, cfg }: Ctx) {
  const at = (s: number, body: ReactNode) => <g transform={`translate(${a.headX} ${a.headTop}) scale(${s})`}>{body}</g>;
  switch (cfg.hat) {
    case 'witch':
      return at(a.hatScale, <g>
        <path d="M-19 7 Q-13 -14 -3 -27 Q3 -35 13 -33 Q18 -32 21 -27 Q13 -29 9.5 -23 Q13 -8 19 7Z" fill={paint('ink')} stroke="#2a1c15" strokeWidth="1" strokeLinejoin="round" />
        <path d="M-17.6 0 Q0 4 17.4 0 L19 7 Q0 11 -19 7Z" fill={paint('orange')} />
        <rect x="-4" y="0.5" width="8" height="6.5" rx="1.6" fill="none" stroke="#f5ce87" strokeWidth="1.6" />
        <path d="M-9 -4 L-3 -20" stroke="#a78a70" strokeWidth="2.2" opacity=".45" strokeLinecap="round" />
        <path d="M-34 9 Q-30 2 0 2 Q30 2 34 9 Q30 15 0 15 Q-30 15 -34 9Z" fill={paint('ink')} stroke="#2a1c15" strokeWidth="1" />
        <path d="M-24 12 Q0 15.5 24 12" fill="none" stroke="#7a6152" strokeWidth="1.2" opacity=".6" strokeLinecap="round" />
      </g>);
    case 'beanie':
      return at(a.hatScale, <g>
        <path d="M-23 6 C-23 -20 23 -20 23 6Z" fill={paint('orange')} stroke="#a9491f" strokeWidth="1" />
        <path d="M-12 4 Q-13 -6 -8 -12 M0 4 V-13.5 M12 4 Q13 -6 8 -12" fill="none" stroke="#c4602c" strokeWidth="1.2" opacity=".7" strokeLinecap="round" />
        <rect x="-25" y="2" width="50" height="10" rx="5" fill={paint('cream')} stroke="#ccb291" strokeWidth="1" />
        <path d="M-18 4 V10 M-12 4 V10 M-6 4 V10 M0 4 V10 M6 4 V10 M12 4 V10 M18 4 V10" stroke="#dcc6a8" strokeWidth="1.1" strokeLinecap="round" />
        <circle cx="0" cy="-16" r="6.5" fill={paint('cream')} stroke="#ccb291" strokeWidth="1" />
        <circle cx="-2" cy="-18" r="2" fill={WHITE} opacity=".9" />
      </g>);
    case 'barista':
      return at(a.hatScale, <g>
        <path d="M-22 8 C-23 -17 23 -17 22 8Z" fill={paint('ink')} stroke="#2a1c15" strokeWidth="1" />
        <path d="M-14 -6 Q-10 -11 -3 -12.5" fill="none" stroke="#8f7564" strokeWidth="2" strokeLinecap="round" opacity=".6" />
        <circle cx="0" cy="-11.4" r="1.8" fill="#6b4c3b" />
        <ellipse cx="0" cy="-1" rx="4" ry="5.2" fill="#f3d9b2" transform="rotate(15 0 -1)" />
        <path d="M-.8 -5.6 Q1.6 -1 -.4 3.6" fill="none" stroke="#6b4430" strokeWidth=".9" strokeLinecap="round" />
        <path d="M-23 7 Q0 2 23 7 Q24 13 0 14 Q-24 13 -23 7Z" fill="#2c1e16" />
        <path d="M-16 9.5 Q0 7 16 9.5" fill="none" stroke="#7a6152" strokeWidth="1" opacity=".7" strokeLinecap="round" />
      </g>);
    case 'bow': {
      const berry = cfg.color === 'berry';
      const fill = berry ? paint('orange') : paint('bow');
      const edge = berry ? '#a9491f' : '#b8475e';
      return at(a.hatScale, <g transform="translate(15 6) rotate(16)">
        <path d="M-1 1 L-6 11 L-2 10 L0 13 L1 2Z M1 1 L6 10.5 L2.5 10 L1.5 13 L0 2Z" fill={fill} stroke={edge} strokeWidth=".8" strokeLinejoin="round" />
        <path d="M0 0 C-5 -8 -14 -7 -13 0 C-12 7 -5 6 0 0Z" fill={fill} stroke={edge} strokeWidth="1" strokeLinejoin="round" />
        <path d="M0 0 C5 -8 14 -7 13 0 C12 7 5 6 0 0Z" fill={fill} stroke={edge} strokeWidth="1" strokeLinejoin="round" />
        <path d="M-10 -2 Q-8 -4 -5 -3.4 M10 -2 Q8 -4 5 -3.4" fill="none" stroke={WHITE} strokeWidth="1.1" opacity=".7" strokeLinecap="round" />
        <ellipse cx="0" cy="0" rx="3.2" ry="3.6" fill={fill} stroke={edge} strokeWidth="1" />
      </g>);
    }
    case 'headphones': {
      const w = a.sideHalf, e = a.earY - a.headTop;
      return at(1, <g>
        <path d={`M${-w + 1} ${e - 4} C${-w} -16 ${w} -16 ${w - 1} ${e - 4}`} fill="none" stroke="#3d2a20" strokeWidth="4.2" strokeLinecap="round" />
        <path d={`M${-w + 4} ${e - 10} C${-w + 4} -11 ${w - 4} -11 ${w - 4} ${e - 10}`} fill="none" stroke="#ffb46a" strokeWidth="1.2" strokeLinecap="round" opacity=".7" />
        <rect x={-w - 5} y={e - 8} width="10" height="16" rx="5" fill={paint('orange')} stroke="#a9491f" strokeWidth="1" />
        <rect x={w - 5} y={e - 8} width="10" height="16" rx="5" fill={paint('orange')} stroke="#a9491f" strokeWidth="1" />
        <path d={`M${-w - 2} ${e - 4} V${e + 3} M${w + 2} ${e - 4} V${e + 3}`} stroke="#ffd29a" strokeWidth="1.4" strokeLinecap="round" opacity=".8" />
      </g>);
    }
    default:
      return null;
  }
}

/* ---------------------------------------------------------------- root */
export interface AvatarArtProps {
  config: Partial<AvatarConfig> | null | undefined;
  size?: number;
  /** Accessible name. Without it the art is decorative (aria-hidden). */
  title?: string;
  className?: string;
}

export function AvatarArt({ config, size = 64, title, className = '' }: AvatarArtProps) {
  const uid = 'av' + useId().replace(/[^a-zA-Z0-9]/g, '');
  const cfg = normalizeAvatar(config);
  const a = AVATAR_ANCHORS[cfg.base as keyof typeof AVATAR_ANCHORS] ?? AVATAR_ANCHORS.ghost;
  const pal = PALETTES[cfg.color] ?? PALETTES.cream;
  const paint: Paint = name => `url(#${uid}-${name})`;
  const human = isHumanBase(cfg.base);
  const skin = SKINS[cfg.skin] ?? SKINS['skin-2'];
  const hair = HAIRS[cfg.hairColor] ?? HAIRS['dark-brown'];
  const ctx: Ctx = { a, pal, paint, cfg, skin, hair, human };
  const tuck = human && cfg.hat in HAT_TUCK ? +(a.headTop + HAT_TUCK[cfg.hat] * a.hatScale).toFixed(2) : null;
  const hairClip = tuck === null ? undefined : `url(#${uid}-hatclip)`;
  // A tall hat needs headroom: shrink the character slightly towards its feet.
  const shrink = cfg.hat === 'witch' ? 'translate(60 110) scale(.9) translate(-60 -110)' : undefined;
  // Human-only keys are omitted on animal bases (they do not draw them).
  const shown = Object.entries(cfg).filter(([k]) => human || !['skin', 'hair', 'hairColor'].includes(k));
  const data = Object.fromEntries(shown.map(([k, v]) => [`data-avatar-${k.toLowerCase()}`, v]));
  const a11y = title ? { role: 'img', 'aria-label': title } : { 'aria-hidden': true as const, focusable: 'false' as const };

  return <svg viewBox="0 0 120 120" width={size} height={size} className={`avatar-art ${className}`.trim()} {...a11y} {...data}>
    <defs>
      <linearGradient id={`${uid}-body`} x1="0" y1="0" x2=".9" y2="1"><stop stopColor={pal.hi} /><stop offset=".55" stopColor={pal.mid} /><stop offset="1" stopColor={pal.lo} /></linearGradient>
      <linearGradient id={`${uid}-orange`} x1="0" y1="0" x2="1" y2="1"><stop stopColor="#ffc173" /><stop offset=".46" stopColor="#e78232" /><stop offset="1" stopColor="#a33e1c" /></linearGradient>
      <linearGradient id={`${uid}-cream`} x1="0" y1="0" x2=".9" y2="1"><stop stopColor="#fffdf4" /><stop offset=".55" stopColor="#f7ead4" /><stop offset="1" stopColor="#d6bb98" /></linearGradient>
      <linearGradient id={`${uid}-ink`} x1="0" y1="0" x2="1" y2="1"><stop stopColor="#796253" /><stop offset=".5" stopColor="#493227" /><stop offset="1" stopColor="#241912" /></linearGradient>
      <linearGradient id={`${uid}-caramel`} x1="0" y1="0" x2="1" y2="1"><stop stopColor="#f6cf8d" /><stop offset=".6" stopColor="#d99c55" /><stop offset="1" stopColor="#a8682f" /></linearGradient>
      {human && <linearGradient id={`${uid}-skin`} x1="0" y1="0" x2=".7" y2="1"><stop stopColor={skin.hi} /><stop offset=".6" stopColor={skin.mid} /><stop offset="1" stopColor={skin.lo} /></linearGradient>}
      {human && <linearGradient id={`${uid}-hair`} x1="0" y1="0" x2=".6" y2="1"><stop stopColor={hair.hi} /><stop offset=".45" stopColor={hair.mid} /><stop offset="1" stopColor={hair.lo} /></linearGradient>}
      {tuck !== null && <clipPath id={`${uid}-hatclip`}><rect x="0" y={tuck} width="120" height={120 - tuck} /></clipPath>}
      <linearGradient id={`${uid}-bow`} x1="0" y1="0" x2="1" y2="1"><stop stopColor="#ffb3c0" /><stop offset=".55" stopColor="#ea7389" /><stop offset="1" stopColor="#bd4a62" /></linearGradient>
    </defs>
    {title && <title>{title}</title>}
    <g data-part="background"><Background id={cfg.background} /></g>
    <g transform={shrink}>
      <ellipse cx="60" cy="108.5" rx="30" ry="4" fill="#39251c" opacity=".12" />
      {human && <g data-part="hair-back" clipPath={hairClip}><g data-hair={cfg.hair}><HairBack style={cfg.hair} hair={hair} paint={paint} /></g></g>}
      <g data-part="body"><Body {...ctx} /></g>
      <g data-part="face"><Face {...ctx} /></g>
      {human && <g data-part="hair-front" clipPath={hairClip}><g data-hair={cfg.hair}><HairFront style={cfg.hair} hair={hair} paint={paint} /></g></g>}
      <g data-part="accessory"><Accessory {...ctx} /></g>
      <g data-part="holding"><Holding {...ctx} /></g>
      <g data-part="hat"><Hat {...ctx} /></g>
    </g>
  </svg>;
}

export default AvatarArt;
